import 'ace-builds/src-noconflict/ace';
import 'ace-builds/webpack-resolver';
import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/mode-sql';
import 'ace-builds/src-noconflict/theme-chrome';

import {
  BasicInput,
  Button,
  Col,
  FAB,
  FormikCheckBox,
  FormikSelect,
  Loading,
  Row,
  SectionTitle,
  useShowNotification
} from '@elotech/components';
import { Informativo } from 'common/components';
import { useCrudRole, useLoading } from 'common/hooks';
import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import AceEditor from 'react-ace';
import styled from 'styled-components';
import * as Yup from 'yup';

import AnaliseInconsistenciaService from '../../service/AnaliseInconsistenciaService';
import { AnaliseInconsistenciaRegra, ValidacaoSqlResultado } from '../../type';
import { severidadeOpcoes } from './SeveridadeChip';

const Editor = styled(AceEditor)`
  * {
    font-family: inherit;
  }
`;

const Negrito = styled.span`
  font-weight: bold;
`;

const validationSchema = Yup.object().shape({
  descricao: Yup.string().required('Descrição é obrigatória').max(200),
  severidade: Yup.string().required('Severidade é obrigatória'),
  sqlConsulta: Yup.string().required('SQL é obrigatório')
});

const regraVazia: AnaliseInconsistenciaRegra = {
  id: undefined,
  descricao: '',
  severidade: 'ERRO',
  sqlConsulta: '',
  ativo: true
};

const MENSAGEM_GENERICA = 'Não foi possível salvar a regra.';

const mensagemDoErro = (error: any): string => {
  const status = error?.response?.status;
  const mensagem = error?.response?.data?.message;
  if ((status === 400 || status === 422) && typeof mensagem === 'string') {
    return mensagem;
  }
  return MENSAGEM_GENERICA;
};

type Props = {
  service: AnaliseInconsistenciaService;
  id: string;
  onConcluir: (alterou: boolean) => void;
};

export const RegraInconsistenciaComponent = ({
  service,
  id,
  onConcluir
}: Props) => {
  const [regra, setRegra] = useState<AnaliseInconsistenciaRegra>(regraVazia);
  const [validacao, setValidacao] = useState<ValidacaoSqlResultado>();
  const [loading, setLoading] = useLoading();
  const showNotification = useShowNotification();
  const isEditing = id !== 'new';
  const permissions = useCrudRole<'analise_inconsistencia_read'>(
    'analise_inconsistencia'
  );
  const podeSalvar = isEditing ? permissions.update : permissions.create;

  useEffect(() => {
    if (isEditing) {
      setLoading(
        service
          .buscarRegra(id)
          .then(({ data }) => setRegra(data))
          .catch(() =>
            showNotification({
              level: 'error',
              message: 'Não foi possível carregar a regra.'
            })
          )
      );
    }
  }, [id, isEditing, service, setLoading]);

  const onSubmit = (valores: AnaliseInconsistenciaRegra) =>
    setLoading(
      service
        .salvarRegra(valores)
        .then(() => onConcluir(true))
        .catch(error =>
          showNotification({ level: 'error', message: mensagemDoErro(error) })
        )
    );

  const validarSql = (sqlConsulta: string) =>
    setLoading(
      service
        .validarSql(sqlConsulta)
        .then(({ data }) => setValidacao(data))
        .catch(() =>
          setValidacao({
            valido: false,
            mensagem: 'Não foi possível validar o SQL.'
          })
        )
    );

  return (
    <>
      <Loading loading={loading} />
      <Formik
        enableReinitialize
        initialValues={regra}
        onSubmit={onSubmit}
        validationSchema={() => validationSchema}
      >
        {formProps => (
          <>
            <Informativo type="INFO" classes="positive" withIcon>
              <p>
                Esta ferramenta é destinada aos técnicos. As alterações feitas
                nesta tela serão substituídas automaticamente nas próximas
                atualizações do sistema.
              </p>
              <p>
                O SQL deve ser um único select que retorna as colunas{' '}
                <Negrito>id</Negrito>, <Negrito>descricao_registro</Negrito> e{' '}
                <Negrito>path_tela</Negrito>. Se precisar da entidade logada use
                a variável $1
              </p>
            </Informativo>
            <SectionTitle marginTop="0px">Dados Gerais</SectionTitle>
            <Row>
              <BasicInput
                size={6}
                maxLength={200}
                name="descricao"
                label="Descrição da ocorrência"
              />
              <FormikSelect
                size={3}
                name="severidade"
                label="Severidade"
                getOptionLabel={option => option.label}
                getOptionValue={option => option.value}
                options={severidadeOpcoes}
              />
              <FormikCheckBox
                size={2}
                className="mt-xs"
                label="Ativo"
                name="ativo"
                id="ativo"
              />
            </Row>
            <Row>
              <Col sm={12}>
                <SectionTitle>SQL</SectionTitle>
                <div className="form-group">
                  {!!formProps.errors.sqlConsulta && (
                    <small className="typography-caption help-message mb-xs">
                      {formProps.errors.sqlConsulta}
                    </small>
                  )}
                  <Editor
                    mode="sql"
                    width="full"
                    theme="chrome"
                    showPrintMargin={false}
                    fontSize={16}
                    onChange={sql => {
                      setValidacao(undefined);
                      formProps.setFieldValue('sqlConsulta', sql);
                    }}
                    value={formProps.values.sqlConsulta}
                    setOptions={{
                      enableBasicAutocompletion: true,
                      enableLiveAutocompletion: true,
                      showLineNumbers: true
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Button
                    type="button"
                    onClick={() => validarSql(formProps.values.sqlConsulta)}
                  >
                    Validar SQL
                  </Button>
                  <Button
                    type="button"
                    color="neutral"
                    onClick={() => onConcluir(false)}
                  >
                    Cancelar
                  </Button>
                </div>
                {validacao && (
                  <Informativo
                    type={validacao.valido ? 'SUCCESS' : 'ERROR'}
                    classes="mt-xs"
                    withIcon
                  >
                    {validacao.valido
                      ? `SQL válido. Colunas: ${(validacao.colunas || []).join(
                          ', '
                        )}.`
                      : validacao.mensagem}
                  </Informativo>
                )}
              </Col>
            </Row>
            {podeSalvar && (
              <div className="btn-save">
                <FAB
                  icon="check"
                  iconColor="white"
                  type="submit"
                  title="Salvar"
                  onClick={formProps.submitForm}
                />
              </div>
            )}
          </>
        )}
      </Formik>
    </>
  );
};
